import React from 'react';

/**
 * ISO27001 component displaying implementation categories and responsibilities
 * @returns {JSX.Element}
 */
function ISO27001() {
  return (
    <div className="max-w-none">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">ISO 27001</h1>
      
      <div className="bg-white rounded-xl shadow-md p-8 border border-gray-100 mb-6">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">ISO 27001 Implementation Categories and Responsibilities</h2>
        
        <div className="bg-blue-50 rounded-lg p-6 mb-8 border-l-4 border-blue-500">
          <h3 className="text-xl font-bold mb-3 text-gray-800">Preamble</h3>
          <p className="text-gray-700 mb-4">
            Kolega is responsible for developing and managing the code base and platform infrastructure. 
            As such, we will handle specific aspects of ISO 27001 compliance related to our technical 
            domain and development practices. However, there will still be requirements for clients to 
            complete aspects of ISO 27001 not related to these technical areas.
          </p>
          <p className="text-gray-700">
            This document outlines the clear division of ISO 27001 implementation responsibilities 
            between Kolega and our clients.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center mb-4">
              <div className="p-3 rounded-full bg-blue-50 text-blue-600 mr-4">
                <span className="text-lg font-bold">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Technical Controls & Infrastructure</h3>
            </div>
            <div className="ml-14">
              <p className="text-blue-700 font-medium mb-3">Our Responsibility</p>
              <p className="text-gray-600 mb-3">
                Technical controls are tangible, configurable security measures that can be objectively 
                verified through testing:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Data encryption (at rest and in transit)</li>
                <li>Firewall configuration and management</li>
                <li>Access control systems</li>
                <li>Vulnerability scanning and patching</li>
                <li>Intrusion detection/prevention</li>
                <li>Logging and monitoring</li>
                <li>Backup and recovery systems</li>
                <li>Network security architecture</li>
                <li>Server hardening</li>
                <li>Database security controls</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center mb-4">
              <div className="p-3 rounded-full bg-blue-50 text-blue-600 mr-4">
                <span className="text-lg font-bold">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Software Development</h3>
            </div>
            <div className="ml-14">
              <p className="text-blue-700 font-medium mb-3">Our Responsibility</p>
              <p className="text-gray-600 mb-3">
                Secure development practices and code-level security controls:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Secure coding standards</li>
                <li>Security testing in development</li>
                <li>Code review processes</li>
                <li>Application security testing</li>
                <li>Dependency management</li>
                <li>Secure API implementation</li>
                <li>Authentication mechanisms</li>
                <li>Input validation</li>
                <li>Output encoding</li>
                <li>Session management</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center mb-4">
              <div className="p-3 rounded-full bg-amber-50 text-amber-600 mr-4">
                <span className="text-lg font-bold">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Organizational Policies</h3>
            </div>
            <div className="ml-14">
              <p className="text-amber-700 font-medium mb-3">Client Responsibility</p>
              <p className="text-gray-600 mb-3">
                Administrative controls that guide organizational behavior:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Information security policies</li>
                <li>HR security procedures</li>
                <li>Asset management policies</li>
                <li>Acceptable use policies</li>
                <li>Security awareness training</li>
                <li>Physical security policies</li>
                <li>Clean desk policy</li>
                <li>Mobile device policies</li>
                <li>Password policies</li>
                <li>Data classification policies</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center mb-4">
              <div className="p-3 rounded-full bg-amber-50 text-amber-600 mr-4">
                <span className="text-lg font-bold">4</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Governance</h3>
            </div>
            <div className="ml-14">
              <p className="text-amber-700 font-medium mb-3">Client Responsibility</p>
              <p className="text-gray-600 mb-3">
                Oversight mechanisms that ensure control effectiveness:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Risk assessment processes</li>
                <li>Compliance monitoring</li>
                <li>Management review</li>
                <li>Internal audit program</li>
                <li>Corrective action processes</li>
                <li>Performance measurement</li>
                <li>Continual improvement</li>
                <li>Documentation management</li>
                <li>Legal and regulatory compliance</li>
                <li>ISMS scope definition</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="mb-8">
          <h3 className="text-2xl font-bold mb-4 text-gray-800">5. Aspects Requiring Integration</h3>
          <p className="text-gray-700 mb-6">
            For the following areas, Kolega and clients must work together to ensure comprehensive coverage. 
            Each party has specific responsibilities that must be coordinated:
          </p>
          
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
            <h4 className="text-xl font-bold mb-4 text-gray-800">Incident Response</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-bold text-blue-700 mb-2">What Kolega will provide:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Technical incident detection</li>
                  <li>Platform-specific response procedures</li>
                  <li>Technical forensics capability</li>
                  <li>Security monitoring and alerting</li>
                  <li>Technical remediation guidance</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-amber-700 mb-2">What clients need to develop:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Organizational incident response plan</li>
                  <li>Escalation procedures</li>
                  <li>Communication plans</li>
                  <li>Business impact assessments</li>
                  <li>Recovery objectives</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
            <h4 className="text-xl font-bold mb-4 text-gray-800">Access Control</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-bold text-blue-700 mb-2">What Kolega will provide:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Authentication mechanisms</li>
                  <li>Authorization controls</li>
                  <li>Role-based access control</li>
                  <li>Privileged access management</li>
                  <li>Access logging and monitoring</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-amber-700 mb-2">What clients need to develop:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Access request and approval processes</li>
                  <li>User access reviews</li>
                  <li>Segregation of duties policies</li>
                  <li>User onboarding/offboarding procedures</li>
                  <li>Acceptable use policies</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
            <h4 className="text-xl font-bold mb-4 text-gray-800">Business Continuity</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-bold text-blue-700 mb-2">What Kolega will provide:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Platform redundancy</li>
                  <li>Backup systems</li>
                  <li>Disaster recovery procedures</li>
                  <li>Technical resilience testing</li>
                  <li>Service level agreements</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-amber-700 mb-2">What clients need to develop:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Business continuity plans</li>
                  <li>Recovery time objectives</li>
                  <li>Business impact analysis</li>
                  <li>Continuity testing and exercises</li>
                  <li>Alternative processing procedures</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
            <h4 className="text-xl font-bold mb-4 text-gray-800">Risk Assessment</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-bold text-blue-700 mb-2">What Kolega will provide:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Technical vulnerability assessments</li>
                  <li>Platform threat modeling</li>
                  <li>Security architecture reviews</li>
                  <li>Technical risk mitigation recommendations</li>
                  <li>Security testing results</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-amber-700 mb-2">What clients need to develop:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Organizational risk assessment methodology</li>
                  <li>Risk treatment plans</li>
                  <li>Risk acceptance criteria</li>
                  <li>Risk register maintenance</li>
                  <li>Regular risk reviews</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
            <h4 className="text-xl font-bold mb-4 text-gray-800">Supplier Management</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-bold text-blue-700 mb-2">What Kolega will provide:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Information about our technical suppliers</li>
                  <li>Security assessments of our critical vendors</li>
                  <li>Third-party risk management for our suppliers</li>
                  <li>Supplier security requirements</li>
                  <li>Monitoring of supplier compliance</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-amber-700 mb-2">What clients need to develop:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Supplier management policy</li>
                  <li>Supplier security requirements</li>
                  <li>Supplier agreements</li>
                  <li>Supplier performance monitoring</li>
                  <li>Supplier termination procedures</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
            <h4 className="text-xl font-bold mb-4 text-gray-800">Asset Management</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-bold text-blue-700 mb-2">What Kolega will provide:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Platform asset inventory</li>
                  <li>Technical asset classification</li>
                  <li>System documentation</li>
                  <li>Data flow diagrams</li>
                  <li>Technical asset handling procedures</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-amber-700 mb-2">What clients need to develop:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Organizational asset inventory</li>
                  <li>Asset ownership assignment</li>
                  <li>Asset handling procedures</li>
                  <li>Media management policies</li>
                  <li>Asset return procedures</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
            <h4 className="text-xl font-bold mb-4 text-gray-800">Training & Awareness</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-bold text-blue-700 mb-2">What Kolega will provide:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Platform-specific security guidance</li>
                  <li>Technical documentation</li>
                  <li>Security feature tutorials</li>
                  <li>Best practice recommendations</li>
                  <li>Security update notifications</li>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-amber-700 mb-2">What clients need to develop:</h5>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Security awareness program</li>
                  <li>Role-based security training</li>
                  <li>Security policy communication</li>
                  <li>Compliance training</li>
                  <li>Security incident response training</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ISO27001;
